/* eslint-disable @typescript-eslint/member-ordering */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { ProtocolQ, ProtocolAttachmentFilter, ProtocolAuditFilter, ProtocolQFilter } from '../model/protocol';

import { DexieService } from './dexieService';
import { HttpUtils } from '../utils/httpUtils';
import { Injectable } from '@angular/core';
import { OnlineService } from './online.service';
import { ProtocolReportFilter } from '../model/attachment';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';

@Injectable()
export class ProtocolQService {

    constructor(private http: HttpClient,
        private dexieService: DexieService,
        private onlineService: OnlineService) {
    }

    findAll(filter: ProtocolQFilter): Observable<any> {
        const url = environment.qualificatesUrl + '/protocol/';
        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getExcel(filter: ProtocolQFilter): Observable<any> {
        const url = environment.qualificatesUrl + '/protocol/excel';

        return this.http.post(url, filter, { responseType: 'blob' });
    }

    findAllAssignable(filter: ProtocolQFilter): Observable<any> {
        const url = environment.qualificatesUrl + '/protocol/assignable';

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    findOne(id: number): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${id}`;
        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }


    findOneShort(id: number): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/short/${id}`;
        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getAudit(id: number, filter: ProtocolAuditFilter): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${id}/audit`;

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    save(protocol: ProtocolQ, token: string, reason: string): Observable<any> {
        return this.genericSave(protocol, token, reason);
    }

    signInfo(prot: ProtocolQ): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${prot.id}/signInfo`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    addSign(prot: ProtocolQ, token: string, signTypes: number[], user: string, lang: string): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${prot.id}/addSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('user', user);
        form.append('signTypes', signTypes.join(';'));
        form.append('lang', lang);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    refuseToSign(prot: ProtocolQ, token: string, reason: string): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${prot.id}/refuseToSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('reason', reason);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    manualSign(prot: ProtocolQ, file: File, token: string): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${prot.id}/manualSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('document', file, file.name);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    automaticSign(prot: ProtocolQ, token: string, signTypes: number[], lang: string): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${prot.id}/automaticSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('signTypes', signTypes.join(';'));
        form.append('lang', lang);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    delete(id: number): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${id}`;

        return this.http.delete(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    downloadPdf(id: number, config: ProtocolReportFilter = new ProtocolReportFilter()): Observable<any> {
        const url = environment.qualificatesUrl + `/report/protocol/${id}`;

        const headers = new HttpHeaders({
            lang: config.lang
        });

        const data = {
            reason: config.reason,
            reviewBy: config.reviewBy,
            toSign: config.toSign,
            hybride: false
        };

        return this.http.post(url, data, { responseType: 'blob', headers });
    }

    downloadPdfProtocolVersion(protocol: ProtocolQ): Observable<any> {
        return this.downloadPdfVersion(protocol.id, protocol.signedVersion);
    }

    downloadPdfVersion(idProtocol: number, version: number): Observable<any> {
        const url = environment.qualificatesUrl + `/report/protocol/${idProtocol}/version/${version}`;

        return this.http.post(url, {}, { responseType: 'blob' });
    }

    downloadAuditPdf(id: number): Observable<any> {
        const url = environment.qualificatesUrl + `/report/protocol/${id}/audit`;

        return this.http.get(url, { responseType: 'blob' });
    }

    getAttachments(id: number, filter: ProtocolAttachmentFilter): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${id}/attachment`;

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getAreas(id: number): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${id}/area`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    moveAttachment(id: number, previousIndex: number, currentIndex: number): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${id}/attachment/move`;

        return this.http.post(url, { previousIndex, currentIndex }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    revertSign(idProtocol: number, reason: string, token: string): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/${idProtocol}/revertSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('reason', reason);
        
        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }
    checkCopyToGroup(id: number, idGroup: number): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/clonate/${id}/toGroup/${idGroup}/check`;

        return this.http.post(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    copyToGroup(id: number, idGroup: number): Observable<any> {
        const url = environment.qualificatesUrl + `/protocol/clonate/${id}/toGroup/${idGroup}`;

        return this.http.put(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    private genericSave(protocol: ProtocolQ, token: string, reason: string, action?: string): Observable<any> {
        let url = environment.qualificatesUrl + '/protocol/';

        if (protocol.id != null) {
            url += protocol.id;

            if (action != null) {
                url += action;
            }

            return this.http.put(url, {protocol, token, reason}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            url += 'create';

            return this.http.post(url, {protocol, token}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        }
    }

    downloadPdfHybride(id: number, config: ProtocolReportFilter = new ProtocolReportFilter()): Observable<any> {
        const url = environment.qualificatesUrl + `/report/protocol/hybride/${id}`;

        const headers = new HttpHeaders({
            lang: config.lang
        });

        const data = {
            hybride: true,
            reason: config.reason,
            reviewBy: config.reviewBy,
            toSign: config.toSign
        };

        return this.http.post(url, data, { responseType: 'blob', headers });
    }

    copyProtocol(id: number): Observable<any>{
        const url = environment.qualificatesUrl + `/protocol/copy/${id}`

        
        return this.http.post(url, id).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    generateReportSignatureTemp(idProtocol: number, version: number) {
        const url = `${environment.qualificatesUrl}/report/protocol/${idProtocol}/${version}/temporalSignature`;
        return this.http.post(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    generateReportExecutionSignatureTemp(idExecution: number, version: number) {
        const url = `${environment.qualificatesUrl}/report/execution/${idExecution}/${version}/temporalSignature`;
        return this.http.post(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

}
